import { useTranslation } from "react-i18next";
import { AanmeldShell } from "@/components/AanmeldShell";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { ChangeWachtwoord, SetWachtwoord } from "@/features/account/Wachtwoord";
import { useAccount } from "@/features/account/useAccount";
import type { Me } from "@/features/session/toegang";

/**
 * Who we think you are - including the address JBNL holds, which a volunteer can find nowhere
 * else - and the wachtwoord block. Also what became of self-service reset.
 * See docs/adr/0066-an-inloglink-is-the-fourth-door-and-a-password-becomes-optional.md.
 */
export function AccountPage() {
  const { t } = useTranslation(["account", "common"]);
  const { data: account, isPending, error } = useAccount();

  return (
    <AanmeldShell titel={t("titel")} uitleg={t("uitleg")}>
      {(me) =>
        isPending ? (
          <PageSkeleton />
        ) : !account ? (
          <Card>
            <CardHeader>
              <CardTitle>{t("errors.titel")}</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : t("errors.load", { status: 0 })}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <AccountOverzicht me={me} account={account} />
        )
      }
    </AanmeldShell>
  );
}

function AccountOverzicht({
  me,
  account,
}: {
  me: Me;
  account: NonNullable<ReturnType<typeof useAccount>["data"]>;
}) {
  const { t } = useTranslation(["account", "common"]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{me.name}</CardTitle>
          <CardDescription>{t("wieUitleg")}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{t("adres")}</dt>
            <dd className="font-medium">{account.email}</dd>
            {me.korpsen.length > 0 && (
              <>
                <dt className="text-muted-foreground">{t("korpsen")}</dt>
                <dd>{me.korpsen.map((korps) => korps.name).join(", ")}</dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("wachtwoord.titel")}</CardTitle>
          <CardDescription>
            {account.hasPassword ? t("wachtwoord.heeft") : t("wachtwoord.geen")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {account.hasPassword ? <ChangeWachtwoord /> : <SetWachtwoord />}
        </CardContent>
      </Card>
    </div>
  );
}
